import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, List, ListItem, Button, Chip, Stack, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Collapse } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';
import { MoreVert, Block, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WeatherSummary from '../shared/WeatherSummary';
import { Task, Plant, WeatherDay, Space } from '../../types/shared';
import { getTaskTypeConfig } from '../../config/taskTypeRegistry';
import { getLocalDateString } from '../../utils/dateUtils'; // Added this import based on the instruction's context

interface DailyGardenScheduleProps {
    weatherDays: WeatherDay[];
    tasks: Task[];
    plants: Plant[];
    spaces: Space[];
    onUpdateStatus: (taskId: string, status: 'completed' | 'skipped' | 'cancelled' | 'pending') => void;
    onCompleteWithPayload: (task: Task) => void;
    onEditTask: (task: Task) => void;
    onDeleteTask: (taskId: string) => void;
    location: string;
}

const DailyGardenSchedule: React.FC<DailyGardenScheduleProps> = ({
    weatherDays,
    tasks,
    plants,
    spaces,
    onUpdateStatus,
    onCompleteWithPayload,
    onEditTask,
    onDeleteTask,
    location
}) => {
    const { t, i18n } = useTranslation();
    const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState<{ [taskId: string]: HTMLElement | null }>({});

    // Group all tasks by date, including completed ones
    const tasksByDate = tasks.reduce((acc, task) => {
        if (!acc[task.date]) acc[task.date] = [];
        acc[task.date].push(task);
        return acc;
    }, {} as Record<string, Task[]>);

    // Sort tasks within each date by priority
    Object.keys(tasksByDate).forEach(date => {
        tasksByDate[date].sort((a, b) => (a.priority || 3) - (b.priority || 3));
    });

    // Create a unified list of dates to display
    // 1. All dates from weatherDays
    // 2. All dates from tasks
    // 3. Make sure today is included even if no tasks or weather
    const todayStr = getLocalDateString();
    const uniqueDates = new Set<string>([
        todayStr,
        ...weatherDays.map(d => d.date),
        ...Object.keys(tasksByDate)
    ]);

    const sortedDates = Array.from(uniqueDates).filter(d => d >= todayStr).sort();

    return (
        <Stack spacing={2}>
            {sortedDates.map((dateStr) => {
                const dayWeather = weatherDays.find(w => w.date === dateStr);
                const allDayTasks = tasksByDate[dateStr] || [];
                const pendingTasks = allDayTasks.filter(t => t.status === 'pending');
                const completedTasks = allDayTasks.filter(t => t.status === 'completed');

                const isToday = dateStr === todayStr;
                const isAllDone = allDayTasks.length > 0 && pendingTasks.length === 0 && completedTasks.length > 0;

                return (
                    <Card key={dateStr} sx={{
                        bgcolor: isToday ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 4,
                        border: isToday ? '2px solid' : 'none',
                        borderColor: 'primary.main',
                        boxShadow: isToday ? '0 8px 32px 0 rgba(31, 38, 135, 0.3)' : '0 4px 12px rgba(0,0,0,0.05)'
                    }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                                    <Typography variant="subtitle2" className="serif-text" sx={{ fontWeight: 'bold' }}>
                                        {new Date(dateStr).toLocaleDateString(i18n.language, { weekday: 'short' })}
                                    </Typography>
                                    <Typography variant="caption" className="serif-text" color="text.secondary">
                                        {new Date(dateStr).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                                    </Typography>
                                </Box>
                                {dayWeather ? (
                                    <WeatherSummary
                                        minTemp={dayWeather.minTemp}
                                        maxTemp={dayWeather.maxTemp}
                                        precipitation={dayWeather.precipitation}
                                        description={dayWeather.description}
                                    />
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {t('tasks.no_weather')}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                {pendingTasks.length > 0 ? (
                                    <Chip label={t('common.pending_tasks', { count: pendingTasks.length })} size="small" color="primary" />
                                ) : isAllDone ? (
                                    <Typography variant="caption" color="text.secondary">{t('common.all_done')}</Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">{t('common.no_tasks')}</Typography>
                                )}
                            </Box>
                        </Box>


                        <Collapse in={pendingTasks.length > 0} unmountOnExit>
                            <CardContent sx={{ pt: 0 }}>
                                <List disablePadding component={TransitionGroup}>
                                    {pendingTasks.map((task) => (
                                        <Collapse key={task.id}>
                                            <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 1.5, p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.5)', borderRadius: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                                                    <Typography variant="body2" component="h3" className="serif-text" sx={{ fontWeight: 'bold' }}>
                                                        {task.title || task.description}
                                                    </Typography>

                                                </Box>

                                                {task.title && (
                                                    <Typography variant="caption" className="serif-text" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                        {task.description}
                                                    </Typography>
                                                )}

                                                {(task.plantIds || (task.payload && (task.payload as any).plantIds)) && (
                                                    <Typography variant="caption" className="serif-text" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                                                        {plants.filter(p => (task.plantIds || []).includes(p.id)).map(p => p.name).join(', ')}
                                                    </Typography>
                                                )}

                                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                                    {(() => {
                                                        const taskConfig = getTaskTypeConfig(task.type);
                                                        const hasForm = taskConfig.fields.length > 0;
                                                        return (
                                                            <>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="success"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (hasForm) {
                                                                            onCompleteWithPayload(task);
                                                                        } else {
                                                                            onUpdateStatus(task.id, 'completed');
                                                                        }
                                                                    }}
                                                                    sx={{ fontSize: '0.7rem', py: 0.2 }}
                                                                >
                                                                    {t(taskConfig.buttonLabelKey)}
                                                                </Button>
                                                                {taskConfig.showSkipButton && (
                                                                    <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); onUpdateStatus(task.id, 'skipped'); }} sx={{ fontSize: '0.7rem', py: 0.2 }}>{t('common.not_today')}</Button>
                                                                )}
                                                                {taskConfig.showOverflowMenu && (
                                                                    <>
                                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setTaskMenuAnchorEl(prev => ({ ...prev, [task.id]: e.currentTarget })); }}>
                                                                            <MoreVert fontSize="small" />
                                                                        </IconButton>
                                                                        <Menu
                                                                            anchorEl={taskMenuAnchorEl[task.id] || null}
                                                                            open={Boolean(taskMenuAnchorEl[task.id])}
                                                                            onClose={() => setTaskMenuAnchorEl(prev => ({ ...prev, [task.id]: null }))}
                                                                        >
                                                                            <MenuItem onClick={() => { setTaskMenuAnchorEl(prev => ({ ...prev, [task.id]: null })); onEditTask(task); }}>
                                                                                <ListItemIcon><MoreVert fontSize="small" /></ListItemIcon>
                                                                                <ListItemText>{t('common.edit')}</ListItemText>
                                                                            </MenuItem>
                                                                            {task.isManual ? (
                                                                                <MenuItem onClick={() => { setTaskMenuAnchorEl(prev => ({ ...prev, [task.id]: null })); onDeleteTask(task.id); }}>
                                                                                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                                                                                    <ListItemText sx={{ color: 'error.main' }}>{t('common.delete')}</ListItemText>
                                                                                </MenuItem>
                                                                            ) : (
                                                                                <MenuItem onClick={() => { setTaskMenuAnchorEl(prev => ({ ...prev, [task.id]: null })); onUpdateStatus(task.id, 'cancelled'); }}>
                                                                                    <ListItemIcon><Block fontSize="small" /></ListItemIcon>
                                                                                    <ListItemText>{t('common.not_relevant')}</ListItemText>
                                                                                </MenuItem>
                                                                            )}
                                                                        </Menu>
                                                                    </>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                            </ListItem>
                                        </Collapse>
                                    ))}
                                </List>
                            </CardContent>
                        </Collapse>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default DailyGardenSchedule;
